/**
 * Property Type List - Display custom property types for a CRM object type
 *
 * Shows a list of custom properties with field type icons and action buttons.
 * Includes skeleton loading state and empty state.
 */

import { useQuery } from '@tanstack/react-query';
import {
  AlertCircle,
  Edit01,
  Plus,
  Settings02,
  Trash01,
} from '@untitledui/icons';
import { memo, useCallback, useEffect } from 'react';

import { useOrg } from '@/contexts/OrgContext';
import { Button } from '@/lib/base/buttons/button';
import { EmptyState } from '@/lib/base/empty-state';
import { FeaturedIcon } from '@/lib/base/foundations/featured-icon/featured-icon';
import { CARD_BORDER } from '@/lib/base/list-card';
import { Table } from '@/lib/base/table';
import { Tooltip, TooltipTrigger } from '@/lib/base/tooltip/tooltip';
import {
  createSchemaClient,
  type FieldType,
  type PropertyType,
} from '@/lib/crm/core/SchemaClient';
import { useFieldTypes } from '@/lib/crm/hooks/useFieldTypes';
import { getFieldTypeLabel } from '@/lib/crm/utils/fieldTypeHelpers';
import { getPropertyTypeIconComponent } from '@/lib/crm/utils/icons/PropertyTypeIcon';
import { cx } from '@/utils/cx';

export const ADMIN_ONLY_PROPERTY_TOOLTIP =
  'Only admins can create, edit, or delete properties.';

export interface PropertyTypeListProps {
  /** The entity type name (contacts, companies, deals) */
  entityTypeName: string;
  /** Callback when edit button is clicked */
  onEdit: (property: PropertyType) => void;
  /** Callback when delete button is clicked */
  onDelete: (property: PropertyType) => void;
  /** Callback when create button is clicked */
  onCreate: () => void;
  /** Whether the user has permission to create property types */
  canCreate?: boolean;
  /** Whether the user has permission to edit property types */
  canEdit?: boolean;
  /** Whether the user has permission to delete property types */
  canDelete?: boolean;
}

/**
 * Skeleton loading row component.
 */
function SkeletonRow({ id }: { id: string }) {
  return (
    <Table.Row id={id}>
      <Table.Cell>
        <div className='h-4 bg-bg-tertiary rounded animate-pulse w-32' />
      </Table.Cell>
      <Table.Cell>
        <div className='flex items-center gap-2'>
          <div className='h-4 w-4 bg-bg-tertiary rounded animate-pulse' />
          <div className='h-4 bg-bg-tertiary rounded animate-pulse w-16' />
        </div>
      </Table.Cell>
      <Table.Cell>
        <div className='flex items-center gap-2 justify-end'>
          <div className='h-8 w-8 bg-bg-tertiary rounded animate-pulse' />
          <div className='h-8 w-8 bg-bg-tertiary rounded animate-pulse' />
        </div>
      </Table.Cell>
    </Table.Row>
  );
}

interface PropertyRowProps {
  property: PropertyType;
  onEdit: (property: PropertyType) => void;
  onDelete: (property: PropertyType) => void;
  canEdit: boolean;
  canDelete: boolean;
  fieldTypes: FieldType[];
}

const PropertyRow = memo(function PropertyRow({
  property,
  onEdit,
  onDelete,
  canEdit,
  canDelete,
  fieldTypes,
}: PropertyRowProps) {
  const handleEdit = useCallback(() => onEdit(property), [onEdit, property]);
  const handleDelete = useCallback(
    () => onDelete(property),
    [onDelete, property]
  );

  const FieldIcon = getPropertyTypeIconComponent(property);
  const fieldTypeLabel = getFieldTypeLabel(
    property.field_type_name,
    fieldTypes
  );
  const editTooltip = `Edit ${property.label} property`;
  const deleteTooltip = `Delete ${property.label} property`;

  return (
    <Table.Row id={property.property_type_name}>
      <Table.Cell>
        <span className='text-sm font-medium text-primary'>
          {property.label}
        </span>
      </Table.Cell>
      <Table.Cell>
        <div className='flex items-center gap-2'>
          <FieldIcon className='h-4 w-4 text-tertiary' />
          <span className='text-sm text-secondary'>{fieldTypeLabel}</span>
        </div>
      </Table.Cell>
      <Table.Cell>
        <div className='flex items-center gap-2 justify-end'>
          <Tooltip title={canEdit ? editTooltip : ADMIN_ONLY_PROPERTY_TOOLTIP}>
            <TooltipTrigger
              aria-label={canEdit ? editTooltip : ADMIN_ONLY_PROPERTY_TOOLTIP}
              onPress={canEdit ? handleEdit : undefined}
              aria-disabled={!canEdit ? true : undefined}
              className={
                canEdit
                  ? 'cursor-pointer p-2 rounded-md hover:bg-bg-tertiary transition-colors text-tertiary hover:text-primary'
                  : 'p-2 rounded-md cursor-not-allowed text-fg-disabled'
              }
            >
              <Edit01 className='h-4 w-4' />
            </TooltipTrigger>
          </Tooltip>
          <Tooltip
            title={canDelete ? deleteTooltip : ADMIN_ONLY_PROPERTY_TOOLTIP}
          >
            <TooltipTrigger
              aria-label={
                canDelete ? deleteTooltip : ADMIN_ONLY_PROPERTY_TOOLTIP
              }
              onPress={canDelete ? handleDelete : undefined}
              aria-disabled={!canDelete ? true : undefined}
              className={
                canDelete
                  ? 'cursor-pointer p-2 rounded-md hover:bg-utility-error-50 transition-colors text-tertiary hover:text-utility-error-600'
                  : 'p-2 rounded-md cursor-not-allowed text-fg-disabled'
              }
            >
              <Trash01 className='h-4 w-4' />
            </TooltipTrigger>
          </Tooltip>
        </div>
      </Table.Cell>
    </Table.Row>
  );
});

/**
 * Property Type List component.
 */
export function PropertyTypeList({
  entityTypeName,
  onEdit,
  onDelete,
  onCreate,
  canCreate = false,
  canEdit = false,
  canDelete = false,
}: PropertyTypeListProps) {
  const { currentOrg } = useOrg();
  const orgId = currentOrg?.id;

  const { data: fieldTypes = [] } = useFieldTypes();

  // Fetch property types using React Query
  const {
    data: propertyTypes,
    isLoading,
    error,
    refetch,
  } = useQuery({
    queryKey: ['propertyTypes', orgId, entityTypeName],
    queryFn: async () => {
      if (!orgId) {
        throw new Error('No organization selected');
      }
      const client = createSchemaClient(orgId);
      return client.getPropertyTypes(entityTypeName);
    },
    enabled: !!orgId,
  });

  const handleRetry = useCallback(() => {
    refetch();
  }, [refetch]);

  useEffect(() => {
    if (error) {
      console.error(error);
    }
  }, [error]);

  // Filter to show only custom properties (where entity_id is not null)
  const customProperties = (propertyTypes || []).filter(
    prop => prop.entity_id !== null
  );

  // Loading state - show skeleton rows
  if (isLoading) {
    return (
      <div className='overflow-hidden rounded-lg border border-border-secondary'>
        <Table aria-label='Custom properties' size='sm'>
          <Table.Header>
            <Table.Head label='Label' isRowHeader />
            <Table.Head label='Field Type' />
            <Table.Head label='Actions' align='right' />
          </Table.Header>
          <Table.Body>
            {[1, 2, 3, 4].map(i => (
              <SkeletonRow key={i} id={`skeleton-${i}`} />
            ))}
          </Table.Body>
        </Table>
      </div>
    );
  }

  // Error state - show error banner with retry button
  if (error) {
    return (
      <div className='rounded-lg border border-utility-error-200 bg-utility-error-50 p-4'>
        <div className='flex items-start gap-3'>
          <FeaturedIcon
            icon={AlertCircle}
            size='sm'
            theme='light-outline'
            color='error'
          />
          <div className='flex-1'>
            <h4 className='text-sm font-medium text-utility-error-700'>
              We couldn't load properties
            </h4>
            <p className='text-sm text-utility-error-600 mt-1'>
              Try again, and get in touch if it keeps happening.
            </p>
          </div>
          <Button size='sm' color='secondary' onClick={handleRetry}>
            Retry
          </Button>
        </div>
      </div>
    );
  }

  // Empty state - show message with create button
  if (customProperties.length === 0) {
    return (
      <div className={cx(CARD_BORDER, 'bg-bg-secondary')}>
        <EmptyState
          icon={
            <FeaturedIcon
              icon={Settings02}
              size='lg'
              theme='light-outline'
              color='gray'
            />
          }
          title='No custom properties for this type'
          description={
            canCreate
              ? 'Create your first custom property to start tracking additional data.'
              : 'No custom properties have been created for this type yet.'
          }
          action={{
            label: 'Create property',
            onClick: onCreate,
            icon: Plus,
            color: 'primary',
            isDisabled: !canCreate,
            disabledTooltip: ADMIN_ONLY_PROPERTY_TOOLTIP,
          }}
        />
      </div>
    );
  }

  // Property list
  return (
    <div className='overflow-hidden rounded-lg border border-border-secondary'>
      <Table aria-label='Custom properties' size='sm'>
        <Table.Header>
          <Table.Head label='Label' isRowHeader />
          <Table.Head label='Field Type' />
          <Table.Head label='Actions' align='right' />
        </Table.Header>
        <Table.Body>
          {customProperties.map(property => (
            <PropertyRow
              key={property.property_type_name}
              property={property}
              onEdit={onEdit}
              onDelete={onDelete}
              canEdit={canEdit}
              canDelete={canDelete}
              fieldTypes={fieldTypes}
            />
          ))}
        </Table.Body>
      </Table>
    </div>
  );
}
